import { z } from 'zod'

import { nanoidSchema, semverSchema } from '@/utility'
import { baseRegistrySchema } from '@/registry'
import { scalarRuleset } from '@/rule'

export enum RegistryAsset {
  Apis = 'apis',
  Schemas = 'schemas',
  Rules = 'rules',
  Sdks = 'sdks',
}

export const managedDocVersionSchema = z.object({
  uid: nanoidSchema,
  /** Millisecond base timestamp of versions  */
  createdAt: z.number(),
  version: semverSchema,
  tags: z.string().array().default([]),
  corpusUid: nanoidSchema.nullable().default(null),
})

export type ManagedDocVersion = z.infer<typeof managedDocVersionSchema>

export const managedDocSchema = baseRegistrySchema.extend({
  version: semverSchema,
  /**
   * Certain types of collections will be write restricted for properties
   * that would mutate the original specification (such as adding endpoints)
   */
  isExternal: z.boolean().default(true),
  /** Repository information if we are managing a github spec */
  repository: z
    .object({
      linkedBy: z.string(),
      id: z.number(),
      name: z.string().min(2),
      path: z.string().default(''),
      branch: z.string().default(''),
    })
    .optional(),
  /** URL to the source spec if we are managing a hosted spec */
  sourceUrl: z.string().url().optional(),
  activeVersionUid: nanoidSchema,
  /** Theme assigned to API, supports custom theme slugs */
  theme: z.string().default(''),
  /**
   * UID to the collaborative YJS doc
   *
   * This content will be replaced on submitted updates and able to be edited in real-time
   * When a user is ready to commit their changes they can bump the version with the changed content
   */
  editableUid: z.string(),
  ruleset: z.string().default(scalarRuleset),
  /**
   * An ordered version history record
   *
   * When a sync update is requested we find any versions with timestamps
   * later than the local base and determine the mutation records
   *
   * Mutations records will then need to be diffed and conflicts handled locally
   * before pushing to the database
   */
  versions: managedDocVersionSchema.array(),
})

export type ManagedDoc = z.infer<typeof managedDocSchema>
